/**
 * Hook do Painel WL para gerenciar Identidade Visual (branding).
 *
 * Todo I/O passa pelas RPCs exclusivas `wl_get_branding` e `wl_update_branding`,
 * que aplicam `wl_assert_permission('wl.settings', view|edit)`. Não há
 * SELECT/UPDATE direto na tabela `system_settings`.
 *
 * Upload de imagens é feito via Supabase Storage (bucket `anexos`, path
 * `system/{type}.{ext}`) — mesma origem usada pelo painel admin, já que a
 * identidade visual é única no sistema.
 */
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { toast } from '@/hooks/use-toast';
import { createLogger } from '@/lib/logger';

const logger = createLogger('WlBranding');

export interface WlBrandingSettings {
  logo_url: string | null;
  icon_url: string | null;
  login_logo_url: string | null;
  app_name: string;
}

const DEFAULT_BRANDING: WlBrandingSettings = {
  logo_url: null,
  icon_url: null,
  login_logo_url: null,
  app_name: 'UaizyChat',
};

const QUERY_KEY = ['wl', 'branding'] as const;

export function useWlBrandingSettings() {
  const queryClient = useQueryClient();

  // Leitura via RPC wl_get_branding
  const query = useQuery({
    queryKey: QUERY_KEY,
    queryFn: async (): Promise<WlBrandingSettings> => {
      const { data, error } = await supabase.rpc('wl_get_branding');
      if (error) {
        logger.error('Erro ao buscar branding WL', error);
        throw error;
      }
      const row = Array.isArray(data) ? data[0] : null;
      return {
        logo_url: row?.logo_url ?? null,
        icon_url: row?.icon_url ?? null,
        login_logo_url: row?.login_logo_url ?? null,
        app_name: row?.app_name || 'UaizyChat',
      };
    },
    staleTime: 5 * 60 * 1000,
  });

  // Atualização via RPC wl_update_branding (envia estado completo)
  const updateMutation = useMutation({
    mutationFn: async (next: Partial<WlBrandingSettings>) => {
      const current = query.data ?? DEFAULT_BRANDING;
      const merged: WlBrandingSettings = { ...current, ...next };
      const { error } = await supabase.rpc('wl_update_branding', {
        p_app_name: merged.app_name,
        p_logo_url: merged.logo_url,
        p_icon_url: merged.icon_url,
        p_login_logo_url: merged.login_logo_url,
      });
      if (error) {
        logger.error('Erro ao atualizar branding WL', error);
        throw error;
      }
      return merged;
    },
    onSuccess: (data) => {
      queryClient.setQueryData(QUERY_KEY, data);
      // Invalida também a chave do admin para refletir em toda a app
      queryClient.invalidateQueries({ queryKey: ['system-settings', 'branding'] });
      toast({
        title: 'Configurações salvas',
        description: 'A identidade visual foi atualizada com sucesso.',
      });
    },
    onError: (err: Error) => {
      toast({
        title: 'Erro ao salvar',
        description: err.message || 'Não foi possível salvar as configurações.',
        variant: 'destructive',
      });
    },
  });

  /**
   * Faz upload da imagem no bucket `anexos` (storage), sobrescrevendo o
   * arquivo anterior do mesmo tipo. Retorna a URL pública com cache-bust.
   */
  const uploadBrandingImage = async (
    file: File,
    type: 'logo' | 'icon' | 'login_logo'
  ): Promise<string> => {
    const ext = (file.name.split('.').pop() || 'png').toLowerCase();
    const path = `system/${type}.${ext}`;

    // Remove possíveis variantes anteriores
    const variants = ['png', 'jpg', 'jpeg', 'svg', 'webp'].map((e) => `system/${type}.${e}`);
    await supabase.storage.from('anexos').remove(variants);

    const { error: uploadError } = await supabase.storage
      .from('anexos')
      .upload(path, file, { upsert: true });

    if (uploadError) {
      logger.error('Erro no upload do branding', uploadError);
      throw uploadError;
    }

    const { data } = supabase.storage.from('anexos').getPublicUrl(path);
    return `${data.publicUrl}?t=${Date.now()}`;
  };

  return {
    branding: query.data ?? DEFAULT_BRANDING,
    isLoading: query.isLoading,
    error: query.error,
    refetch: query.refetch,
    updateBranding: updateMutation.mutateAsync,
    isUpdating: updateMutation.isPending,
    uploadBrandingImage,
  };
}
